import React, { RefObject, useMemo } from "react";
import { Button, InformationalModal } from "@leafly-com/frontend-components";
import LinkMaker from "common/linkMaker";
import { DispensaryInformation } from "./DispensaryInformation";
import { getCountryCode } from "redux/selectors/location";
import { useSelector } from "react-redux";
import {
  getCartItemCount,
  getStaleCartDispensaryInformation,
} from "redux/selectors/reservation";
import { Dispensary } from "custom-types/Dispensary";

const LABEL_ID = "new-bag-label";

type NewBagModalProps = {
  newDispensary?: Dispensary;
  onDismissed?: () => void;
  onNewBagClick?: () => void;
  onKeepBagClick?: () => void;
  returnFocusRef: RefObject<{}>;
};

export const NewBagModal: React.FC<NewBagModalProps> = ({
  newDispensary,
  onDismissed = () => {},
  onNewBagClick = () => {},
  onKeepBagClick = () => {},
  ...props
}) => {
  const itemCount = useSelector(getCartItemCount);
  const staleDispensary = useSelector(getStaleCartDispensaryInformation);

  const countryCode = useSelector(getCountryCode);
  const linkMaker = useMemo(
    () =>
      new LinkMaker({
        countryCode,
        retailType: staleDispensary?.retailType,
        slug: staleDispensary?.slug || "",
      }),
    [countryCode, staleDispensary?.retailType, staleDispensary?.slug]
  );

  return (
    <InformationalModal
      aria-labelledby={LABEL_ID}
      onDismissed={onDismissed}
      {...props}
    >
      {({ dismiss }: { dismiss: () => void }) => (
        <>
          <div className="pb-xxl mb-xxl border-b border-light-grey">
            <div id={LABEL_ID} className="heading--m mb-lg">
              One bag, one store at a time
            </div>

            <div>
              You can start a new bag with this store, but it will empty your
              existing bag. Continue?
            </div>

            {newDispensary && (
              <DispensaryInformation dispensary={newDispensary} />
            )}

            <div className="mt-md">
              <Button variant="secondary" onClick={onNewBagClick}>
                yes, start a new bag
              </Button>
            </div>
          </div>

          <div>
            <div className="heading--m mb-lg">Your existing bag</div>
            {staleDispensary && (
              <DispensaryInformation dispensary={staleDispensary}>
                {itemCount && (
                  <div className="font-bold">
                    {itemCount} item{itemCount === 1 ? "" : "s"} in bag
                  </div>
                )}
              </DispensaryInformation>
            )}

            <div className="mt-md">
              <Button
                variant="tertiary"
                onClick={() => {
                  onKeepBagClick();
                  dismiss();
                }}
              >
                no, keep this bag
              </Button>

              <Button
                href={staleDispensary && linkMaker.getMainTabRoute()}
                variant="tertiary"
                className="mt-md flex justify-center items-center"
              >
                <svg height="20" width="20" className="mr-sm">
                  <use href="#bag" xlinkHref="#bag"></use>
                </svg>
                shop this store
              </Button>
            </div>
          </div>
        </>
      )}
    </InformationalModal>
  );
};
